import { useEffect, useState } from 'react';
import { Loader2, Search, UserSearch } from 'lucide-react';
import { DataTablePagination } from '@/components';
import { AnchoredDropdownMenu } from '@/components/ui/AnchoredDropdownMenu';
import { useDebounce } from '@/hooks';
import { useGetProfessionals } from '@/services';
import { formatProfessionalFullName } from '@/utils';
import { cn } from '@/lib/utils';

const PICKER_PAGE_SIZE = 8;

interface ProfessionalUsageSwitcherProps {
	currentProfessionalId: string;
	onSelect: (id: string) => void;
}

const resolveProfessionalInitial = (row: Professionals.Professional) => {
	const source =
		row.first_name?.trim() ||
		row.last_name?.trim() ||
		row.email?.trim()?.charAt(0) ||
		'?';
	return source.charAt(0).toUpperCase();
};

const SwitcherAvatar = ({ row }: { row: Professionals.Professional }) => {
	const [failed, setFailed] = useState(false);
	const initial = resolveProfessionalInitial(row);
	const photoUrl = row.profile_photo_url?.trim();

	useEffect(() => {
		setFailed(false);
	}, [photoUrl]);

	if (photoUrl && !failed) {
		return (
			<img
				src={photoUrl}
				alt=""
				referrerPolicy="no-referrer"
				onError={() => setFailed(true)}
				className="h-7 w-7 shrink-0 rounded-full object-cover ring-1 ring-zinc-200/80"
			/>
		);
	}

	return (
		<div
			className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-zinc-100 text-[11px] font-bold uppercase text-zinc-600 ring-1 ring-zinc-200/80"
			aria-hidden
		>
			{initial}
		</div>
	);
};

const ProfessionalUsageSwitcher = ({
	currentProfessionalId,
	onSelect,
}: ProfessionalUsageSwitcherProps) => {
	const [isOpen, setIsOpen] = useState(false);
	const [search, setSearch] = useState('');
	const [pickerPage, setPickerPage] = useState(1);
	const debouncedSearch = useDebounce(search, 300);

	useEffect(() => {
		if (!isOpen) {
			setSearch('');
			setPickerPage(1);
		}
	}, [isOpen]);

	useEffect(() => {
		setPickerPage(1);
	}, [debouncedSearch]);

	const { data, isLoading, isFetching } = useGetProfessionals(
		{
			page: pickerPage,
			limit: PICKER_PAGE_SIZE,
			...(debouncedSearch.trim()
				? { search: debouncedSearch.trim() }
				: {}),
		},
		{ enabled: isOpen },
	);

	const professionals = data?.data ?? data?.professionals ?? [];
	const totalProfessionals = data?.total ?? 0;
	const showPagination = totalProfessionals > PICKER_PAGE_SIZE;

	const handleSelect = (id: string) => {
		setIsOpen(false);
		if (id !== currentProfessionalId) {
			onSelect(id);
		}
	};

	return (
		<AnchoredDropdownMenu
			open={isOpen}
			onOpenChange={setIsOpen}
			menuClassName="!w-80 animate-in fade-in zoom-in-95 duration-100"
			trigger={({ ref, isOpen: menuOpen, onTriggerClick }) => (
				<button
					ref={ref}
					type="button"
					onClick={onTriggerClick}
					className={cn(
						'inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border border-solid outline-none transition focus:outline-none',
						menuOpen
							? 'border-zinc-500 bg-zinc-100 text-zinc-900'
							: 'border-stone-200 bg-white text-stone-400 hover:border-zinc-400 hover:text-stone-700',
					)}
					aria-label="Selecionar profissional"
					aria-expanded={menuOpen}
					aria-haspopup="menu"
				>
					<UserSearch className="size-3.5" strokeWidth={2.25} />
				</button>
			)}
		>
			<div className="flex flex-col">
				<div className="border-b border-stone-100 px-3 py-2.5">
					<p className="mb-2 text-[10px] font-bold uppercase tracking-wider text-slate-500">
						Trocar profissional
					</p>
					<div className="relative">
						<Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-stone-400" />
						<input
							type="search"
							value={search}
							onChange={(event) => setSearch(event.target.value)}
							placeholder="Pesquisar por nome..."
							className="w-full rounded-lg border border-stone-200 bg-white py-2 pl-8 pr-2 text-xs text-stone-800 outline-none focus:border-zinc-400"
						/>
					</div>
				</div>

				<div className="max-h-64 overflow-y-auto py-1">
					{isLoading || isFetching ? (
						<div className="flex justify-center py-8">
							<Loader2 className="size-5 animate-spin text-stone-400" />
						</div>
					) : professionals.length === 0 ? (
						<p className="px-3 py-6 text-center text-xs text-stone-500">
							Nenhum profissional encontrado
						</p>
					) : (
						professionals.map((professional) => {
							const isSelected =
								professional.id === currentProfessionalId;
							const name =
								formatProfessionalFullName(professional);
							const email = professional.email?.trim() || '—';

							return (
								<button
									key={professional.id}
									type="button"
									onClick={() =>
										handleSelect(professional.id)
									}
									className={cn(
										'flex w-full items-center gap-2.5 px-3 py-2.5 text-left transition hover:bg-stone-50',
										isSelected && 'bg-zinc-50',
									)}
								>
									<SwitcherAvatar row={professional} />
									<div className="min-w-0 flex-1">
										<p
											className="truncate text-xs font-semibold text-stone-800"
											title={name}
										>
											{name}
										</p>
										<p
											className="truncate text-[11px] text-stone-500"
											title={email}
										>
											{email}
										</p>
									</div>
									{isSelected && (
										<span className="shrink-0 text-[10px] font-bold uppercase tracking-wide text-emerald-600">
											Atual
										</span>
									)}
								</button>
							);
						})
					)}
				</div>

				{showPagination && (
					<div className="flex items-center justify-between border-t border-stone-100 px-3 py-2">
						<span className="text-[10px] text-stone-400">
							{totalProfessionals} profissional
							{totalProfessionals !== 1 ? 'is' : ''}
						</span>
						<DataTablePagination
							page={pickerPage}
							pageSize={PICKER_PAGE_SIZE}
							totalItems={totalProfessionals}
							onPageChange={setPickerPage}
						/>
					</div>
				)}
			</div>
		</AnchoredDropdownMenu>
	);
};

export { ProfessionalUsageSwitcher };
